import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { sortPractitioners } from '../../../util/practitionerSorting';
import { formatMoney } from '../../../util/currency';
import { types } from '../../../util/sdkLoader';
import { createSlug } from '../../../util/urlHelpers';
import getCountryCodes from '../../../translations/countryCodes';
import { getPractitionerFromPrice } from '../../../util/practitionerSearchPricing';
import { searchReturnState } from '../../../util/searchReturn';
import { Avatar, NamedLink, PaginationLinks, ReviewRating } from '../../../components';
import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    practitionerSort = 'recommended',
    location,
  } = props;
  const { Money } = types;
  const isWorkshopSearch = ['workshop', 'groups_and_workshops'].includes(listingTypeParam);
  const returnState = searchReturnState(location);
  const returnToSearch = returnState ? `${returnState.pathname}${returnState.search}` : '';

  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };
  const practitioners = Array.from(
    listings
      .reduce((map, listing) => {
        const author = listing.author;
        const authorId = author?.id?.uuid;

        if (!authorId) {
          return map;
        }

        if (map.has(authorId)) {
          map.get(authorId).listings.push(listing);
        } else {
          map.set(authorId, {
            author,
            listings: [listing],
          });
        }

        return map;
      }, new Map())
      .values()
  );
  const sortedPractitioners = sortPractitioners(
    practitioners,
    practitionerSort,
    props.practitionerRatings,
    props.practitionerAvailability
  );

  const currentPage = Math.max(1, Number(pagination?.page) || 1);
  const pageSize = pagination?.perPage || listings.length || 1;
  const pageStart = (currentPage - 1) * pageSize;
  const visiblePractitioners = sortedPractitioners.slice(pageStart, pageStart + pageSize);
  const visibleWorkshops = listings.slice(pageStart, pageStart + pageSize);

  if (isWorkshopSearch) {
    return (
      <div className={classes}>
        <ul className={css.workshopCards}>
          {visibleWorkshops.map(listing => {
            const { title = '', price } = listing.attributes || {};
            const workshopEvent = listing.attributes?.publicData || {};
            const eventStart = workshopEvent.workshopEventStart;
            const eventEnd = workshopEvent.workshopEventEnd;
            const eventTimezone = workshopEvent.workshopEventTimezone;
            const deliveryMode = workshopEvent.workshopEventDeliveryMode;
            const eventLabel =
              eventStart && eventEnd && eventTimezone
                ? new Intl.DateTimeFormat(intl.locale, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: eventTimezone,
                  }).format(new Date(eventStart)) +
                  ' – ' +
                  new Intl.DateTimeFormat(intl.locale, {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: eventTimezone,
                  }).format(new Date(eventEnd))
                : null;
            const displayName = listing.author?.attributes?.profile?.displayName;
            const image = listing.images?.[0];
            const imageUrl =
              image?.attributes?.variants?.['listing-card']?.url ||
              image?.attributes?.variants?.['scaled-medium']?.url ||
              image?.attributes?.variants?.['scaled-small']?.url;
            const priceLabel =
              price?.amount != null && price?.currency ? formatMoney(intl, price) : null;
            const params = { id: listing.id.uuid, slug: createSlug(title) };

            return (
              <li key={listing.id.uuid} className={css.resultItem}>
                <article className={css.workshopCard}>
                  <NamedLink name="ListingPage" params={params} to={{ state: { searchReturn: returnState } }} className={css.workshopImageLink}>
                    {imageUrl ? (
                      <img src={imageUrl} alt="" className={css.workshopImage} />
                    ) : (
                      <div className={css.workshopImageFallback} aria-hidden="true" />
                    )}
                  </NamedLink>
                  <div className={css.workshopInfo}>
                    <h3 className={css.workshopTitle}>{title}</h3>
                    {displayName ? <p className={css.workshopPractitioner}>{displayName}</p> : null}
                    {priceLabel ? <p className={css.workshopPrice}>{priceLabel}</p> : null}
                    {eventLabel ? <p className={css.workshopEvent}>{eventLabel}</p> : null}
                    {deliveryMode ? (
                      <p className={css.workshopDeliveryMode}>
                        {deliveryMode === 'online' ? 'Online' : 'In person'}
                      </p>
                    ) : null}
                  </div>
                  <NamedLink name="ListingPage" params={params} to={{ state: { searchReturn: returnState } }} className={css.viewWorkshop}>
                    View workshop
                  </NamedLink>
                </article>
              </li>
            );
          })}
        </ul>
        {paginationLinks}
      </div>
    );
  }

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {visiblePractitioners.map(({ author, listings: practitionerListings }) => {
          const displayName = author?.attributes?.profile?.displayName;

          const ratingData = props.practitionerRatings?.[author.id.uuid] || {
            average: null,
            count: 0,
          };

          const availability = props.practitionerAvailability?.[author.id.uuid] || [];

          const publicData = author?.attributes?.profile?.publicData || {};
          const countryCode = publicData.practitionerCountryCode;
          const country = getCountryCodes(intl.locale).find(item => item.code === countryCode)
            ?.name;
          const practitionerInventory = (props.practitionerCalendarSources || []).filter(
            listing =>
              listing.author?.id?.uuid === author.id.uuid ||
              listing.relationships?.author?.data?.id?.uuid === author.id.uuid
          );
          const fromPrice = getPractitionerFromPrice(
            practitionerListings,
            practitionerInventory,
            props.currency
          );
          const formattedFromPrice = fromPrice
            ? formatMoney(intl, new Money(fromPrice.amount, fromPrice.currency))
            : null;
          const bio = author?.attributes?.profile?.bio || '';

          const sessionOptions = [
            ...new Set(
              practitionerListings.flatMap(listing => {
                const listingPublicData = listing.attributes?.publicData || {};

                const liveOptions = listingPublicData.sessionformat || [];
                const deliveryOptions = listingPublicData.deliveryformat || [];
                const configured = [...liveOptions, ...deliveryOptions];
                if (configured.length) return configured;
                const legacyFallbacks = {
                  '1_1_session': ['video'],
                  private_audio: ['audio'],
                  email: ['written'],
                  recorded_and_written_reading: ['written'],
                };
                return legacyFallbacks[listingPublicData.listingType] || [];              })
            ),
          ];

          const sessionOptionLabels = {
            video: 'Video call',
            audio: 'Audio only',
            written: 'Written',
            recordedvideo: 'Recorded video',
          };

          const sessionOptionOrder = ['video', 'audio', 'written', 'recordedvideo'];

          const sessionOptionNames = sessionOptions
            .sort((a, b) => sessionOptionOrder.indexOf(a) - sessionOptionOrder.indexOf(b))
            .map(option => sessionOptionLabels[option] || option);

          const services = publicData.servicesoffered || [];

          const serviceLabels = services.map(service =>
            service
              .replace(/_/g, ' ')
              .replace(/-/g, ' ')
              .replace(/\b\w/g, char => char.toUpperCase())
          );

          const shortBio = bio.length > 140 ? `${bio.substring(0, 140)}...` : bio;
          return (
            <li key={author.id.uuid} className={css.resultItem}>
              <NamedLink
                name="ProfilePage"
                params={{ id: author.id.uuid }}
                to={{
                  search: returnToSearch ? `?returnTo=${encodeURIComponent(returnToSearch)}` : '',
                  state: { searchReturn: returnState },
                }}
                className={css.practitionerCard}
              >
                <div className={css.practitionerPhotoPanel}>
                  {author.profileImage ? (
                    <img
                      src={author.profileImage.attributes.variants['listing-card']?.url}
                      alt={displayName}
                      className={css.practitionerAvatar}
                    />
                  ) : (
                    <Avatar
                      user={author}
                      rootClassName={css.practitionerAvatar}
                      initialsClassName={css.practitionerInitials}
                      disableProfileLink
                    />
                  )}
                </div>

                <div className={css.practitionerIdentity}>
                  <h3 className={css.practitionerName}>{displayName}</h3>
                  {country ? <span className={css.practitionerCountry}>{country}</span> : null}
                  {ratingData.count > 0 ? (
                    <div className={css.practitionerRating}>
                      <ReviewRating
                        rating={ratingData.average}
                        className={css.reviewStars}
                        reviewStarClassName={css.reviewStar}
                      />
                      <span>
                        {intl.formatMessage(
                          { id: 'SearchResultsPanel.reviewSummary' },
                          { average: ratingData.average.toFixed(1), count: ratingData.count }
                        )}
                      </span>
                    </div>
                  ) : (
                    <span className={css.reviewInvitation}>
                      <svg
                        className={css.detailIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9Z" />
                      </svg>
                      {intl.formatMessage({ id: 'SearchResultsPanel.firstReview' })}
                    </span>
                  )}
                </div>

                <div className={css.practitionerInfo}>
                  {serviceLabels.length > 0 ? (
                    <div className={css.practitionerSpecialisms}>
                      {serviceLabels.slice(0, 3).map(service => (
                        <span key={service} className={css.specialismTag}>
                          {service}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {sessionOptionNames.length > 0 && (
                    <div className={css.sessionOptions}>
                      {sessionOptionNames.map((label, index) => (
                        <span key={sessionOptions[index]} className={css.sessionOption}>
                          {sessionOptions[index] === 'video' ? (
                            <svg
                              className={css.detailIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="M3 6h12v12H3z M15 10l6-3v10l-6-3" />
                            </svg>
                          ) : sessionOptions[index] === 'audio' ? (
                            <svg
                              className={css.detailIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="M4 14v-2a8 8 0 0 1 16 0v2 M4 12h3v8H4z M17 12h3v8h-3z" />
                            </svg>
                          ) : sessionOptions[index] === 'written' ? (
                            <svg
                              className={css.detailIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="M6 3h9l4 4v14H6z M15 3v5h4 M9 12h7 M9 16h5" />
                            </svg>
                          ) : sessionOptions[index] === 'recordedvideo' ? (
                            <svg
                              className={css.detailIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="M4 4h16v16H4z M10 8l6 4-6 4z" />
                            </svg>
                          ) : null}
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                  {shortBio ? <p className={css.practitionerBio}>{shortBio}</p> : null}
                </div>

                <div className={css.practitionerFooter}>
                  {formattedFromPrice ? (
                    <div className={css.fromPrice}>
                      <span className={css.fromPriceLabel}>From</span>
                      <span className={css.fromPriceValue}>{formattedFromPrice}</span>
                    </div>
                  ) : null}
                  {availability.length > 0 ? (
                    <div className={css.nextAvailability}>
                      <span className={css.nextAvailabilityLabel}>
                        <svg
                          className={css.detailIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d="M5 5h14v16H5z M8 3v4 M16 3v4 M5 10h14" />
                        </svg>
                        {intl.formatMessage({ id: 'SearchResultsPanel.nextAvailable' })}
                      </span>
                      <span className={css.nextAvailabilityTime}>
                        {new Date(availability[0].attributes.start).toLocaleString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  ) : null}
                  <span className={css.viewProfile}>
                    {intl.formatMessage({ id: 'SearchResultsPanel.viewProfile' })}
                    <span aria-hidden="true">&rarr;</span>
                  </span>
                </div>
              </NamedLink>
            </li>
          );
        })}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
